<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { DoubleMaterialityIroRiskAndOpportunityType } from '@/__generated__/types';
import useFormatNumber from '@/utils/composables/useFormatNumber';
import AtBadge from '@/components/atoms/AtBadge/AtBadge.vue';
import { BadgeType } from '@/components/atoms/AtBadge/types';
import { useStore } from '../../store';
import OgTable from '../../OgTable.vue';
import {
  getRisksAndOpportunitiesData,
  risksAndOpportunitiesHeaders
} from '@/components/pages/Modules/DoubleMateriality/PgResults/PgList/dataGenerators';
import type { RisksAndOpportunitiesRowItem } from '@/components/pages/Modules/DoubleMateriality/PgResults/PgList/types';

const { t } = useI18n();
const store = useStore();
const { formatNumber } = useFormatNumber();

const headers = computed(() =>
  risksAndOpportunitiesHeaders.map((h) => ({ ...h, text: t(h.text) })),
);

const items = computed<RisksAndOpportunitiesRowItem[]>(() => getRisksAndOpportunitiesData(store.value));
</script>

<template>
  <h3 class="text-lg font-medium mb-3">
    {{ t('Financial materiality') }}
  </h3>
  <OgTable
    :headers="headers"
    :items="items"
    :isRowRemovable="false"
    tableClass="!mb-16"
  >
    <template #item-esrs="row: RisksAndOpportunitiesRowItem">
      <p>{{ row.topic.esrs }} {{ t(row.topic.topic) }}</p>
    </template>
    <template #item-sustainabilityMatter="row: RisksAndOpportunitiesRowItem">
      <p>
        {{ t(row.topic.subSubTopic) || t(row.topic.subTopic) || '' }}
      </p>
    </template>
    <template #item-iro="row: RisksAndOpportunitiesRowItem">
      <p>
        {{ row.iroRiskAndOpportunity.riskAndOpportunity }}
      </p>
    </template>
    <template #item-iroType="row: RisksAndOpportunitiesRowItem">
      <p>
        <template
          v-if="
            row.iroRiskAndOpportunity.type ===
            DoubleMaterialityIroRiskAndOpportunityType.OPPORTUNITY
          "
        >
          {{ t('Opportunity') }}
        </template>
        <template
          v-else-if="
            row.iroRiskAndOpportunity.type ===
            DoubleMaterialityIroRiskAndOpportunityType.RISK
          "
        >
          {{ t('Risk') }}
        </template>
      </p>
    </template>
    <template #item-likelihood="row: RisksAndOpportunitiesRowItem">
      <p>
        {{ row.likelihood ?? t('n/a') }}
      </p>
    </template>
    <template #item-potentialMagnitude="row: RisksAndOpportunitiesRowItem">
      <p>
        {{ row.potentialMagnitude ?? t('n/a') }}
      </p>
    </template>
    <template #item-totalScore="row: RisksAndOpportunitiesRowItem">
      <p>
        {{
          typeof row.totalScore !== 'number'
            ? t('n/a')
            : formatNumber(row.totalScore, 0, 2)
        }}
      </p>
    </template>
    <template #item-materiality="row: RisksAndOpportunitiesRowItem">
      <AtBadge
        :type="
          row.materiality === true
            ? BadgeType.SUCCESS
            : row.materiality === false
              ? BadgeType.WARNING
              : BadgeType.DEFAULT
        "
      >
        {{
          row.materiality === true
            ? t('Material')
            : row.materiality === false
              ? t('Not material')
              : t('Start')
        }}
      </AtBadge>
    </template>
  </OgTable>
</template>
